import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { useShallow } from "zustand/react/shallow";

import { useRoutesUiStore } from "@/components/RoutesProvider";

import { Route } from "../../core/types";
import { useVenueCircuitsByUuid, useVenueLinesByUuid } from "../../hooks/routes";
import { useCurrentVenueUuid } from "../../hooks/venues";
import { getPinGradeValue } from "../../utils/grades";
import toRouteLine from "../../utils/toRouteLine";
import { RouteBulkFormValues, RouteFormValues } from "./formValues";

const SYNC_FIELDS = new Set([
  "gradeValue",
  "colors",
  "scheduledEndTime",
  "circuit",
  "circuit.number",
  "startLine",
  "competition",
]);

type Params = {
  route?: Partial<Route> & { uuid: string };
  routes?: Route[];
  methods: UseFormReturn<RouteFormValues> | UseFormReturn<RouteBulkFormValues>;
};

const emptyArray: never[] = [];

interface DirtyNested {
  [key: string]: boolean | boolean[] | DirtyNested | undefined;
}

const filterDirty = (formData: Record<string, unknown>, dirtyFields: DirtyNested) => {
  const dirtyFormData: Record<string, unknown> = {};
  Object.entries(dirtyFields).forEach(([key, value]) => {
    if (value === true || (Array.isArray(value) && value.some(Boolean))) {
      dirtyFormData[key] = formData[key];
    } else if (value && !Array.isArray(value) && typeof value === "object") {
      const subDirty = filterDirty(formData[key] as Record<string, unknown>, value);
      if (Object.keys(subDirty).length) {
        dirtyFormData[key] = subDirty;
      }
    }
  });
  return dirtyFormData as RouteFormValues | RouteBulkFormValues;
};

export default function useSyncFormWithUiStore({ route, routes, methods }: Params) {
  const { setRouteFormEdit, setRouteFormIsDirty, clearRouteFormEdit } = useRoutesUiStore(
    useShallow((state) => ({
      setRouteFormEdit: state.setRouteFormEdit,
      setRouteFormIsDirty: state.setRouteFormIsDirty,
      clearRouteFormEdit: state.clearRouteFormEdit,
    })),
  );

  const routesArray = useMemo(() => {
    if (route) {
      return [route];
    } else if (routes) {
      return routes;
    }
    return emptyArray;
  }, [route, routes]);

  const watch = methods.watch;
  const currentVenueUuid = useCurrentVenueUuid();

  const dirtyFieldsRef = useRef(methods.formState.dirtyFields);
  useLayoutEffect(() => {
    dirtyFieldsRef.current = methods.formState.dirtyFields;
  }, [methods.formState.dirtyFields]);

  const circuits = useVenueCircuitsByUuid(currentVenueUuid);
  const lines = useVenueLinesByUuid(currentVenueUuid);

  // Sync the dirty state with the UI store
  useEffect(() => {
    setRouteFormIsDirty(methods.formState.isDirty);
    if (methods.formState.isDirty) {
      const onBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = "";
      };
      addEventListener("beforeunload", onBeforeUnload);
      return () => removeEventListener("beforeunload", onBeforeUnload);
    }
  }, [methods.formState.isDirty, setRouteFormIsDirty]);

  useEffect(() => {
    if (!routesArray.length) {
      return;
    }
    const subscription = watch((formData, { name, type }) => {
      const dirtyFormData = filterDirty(formData, dirtyFieldsRef.current);

      console.log("watch", { formData, name, type });
      if (name && SYNC_FIELDS.has(name)) {
        const circuit =
          typeof formData.circuit === "string"
            ? circuits?.[formData.circuit]
            : circuits?.[formData.circuit?.uuid || ""];
        const line = lines?.[formData.startLine || ""];
        routesArray.forEach((route) => {
          setRouteFormEdit(route.uuid, {
            ...("name" in dirtyFormData && dirtyFormData.name ? { name: dirtyFormData.name } : {}),
            // gradeValue: formData.gradeValue,
            ...(dirtyFormData.gradeValue ? { gradeValue: dirtyFormData.gradeValue } : {}),
            ...(dirtyFormData.gradeSystem ? { gradeSystem: dirtyFormData.gradeSystem } : {}),
            ...(dirtyFormData.gradeValue && route.gradeSystem
              ? {
                  pinGradeValue: getPinGradeValue(
                    dirtyFormData.gradeValue ?? null,
                    dirtyFormData.gradeSystem || route.gradeSystem,
                  ),
                }
              : {}),
            ...(dirtyFormData.competition
              ? {
                  competitionRouteGroups: [
                    {
                      competition: dirtyFormData.competition,
                      uuid: "",
                      name: "",
                      competitionName: "",
                      categories: [],
                      leagueName: null,
                    },
                  ],
                }
              : {}),
            ...(dirtyFormData.colors ? { colors: dirtyFormData.colors } : {}),
            ...(dirtyFieldsRef.current.circuit && circuit
              ? {
                  circuit: {
                    uuid: circuit?.uuid,
                    name: circuit?.name || "",
                    number: formData.circuit?.number ?? route.circuitNumber ?? null,
                    difficulty: formData.circuit?.difficulty ?? route.circuit?.difficulty ?? null,
                    routeDisplay: circuit?.routeDisplay || "NUMBER",
                  },
                }
              : {}),
            ...(typeof dirtyFormData.circuit === "object" && dirtyFormData.circuit?.number
              ? { circuitNumber: formData.circuit?.number }
              : {}),
            ...(dirtyFieldsRef.current.startLine && line ? { startLine: toRouteLine(line) } : {}),
          });
        });
      }
    }) as { unsubscribe: () => void };
    return () => {
      subscription.unsubscribe();
      // routesArray.forEach((route) => clearRouteFormEdit(route.uuid));
    };
  }, [routesArray, watch, setRouteFormEdit, clearRouteFormEdit, circuits, lines]);
}
